import React, { ReactNode, useState } from "react"
import clsx from "clsx"
import { Box, Row } from "ui/components/atoms/_atoms"
import { IconButton } from "ui/components/atoms/button"
import Icon, { IconBox } from "ui/components/atoms/icon"
import { H3, Label } from "ui/components/atoms/typography"
import { BaseStyles } from "ui/types/base-styles"
import { generateStylesFromProps } from "ui/utils/styles"

import styles from "./Accordion.module.css"

export const AccordionCheckbox = ({
  disabled,
  checked,
  onClick,
  $borderColor,
  className = "",
  style = {},
  ...rest
}: {
  disabled?: boolean
  checked: boolean
  onClick: () => void
  className?: string
  style?: React.CSSProperties
} & BaseStyles) => {
  const generatedStyles = generateStylesFromProps({ ...rest, $borderColor })

  const checkClasses = clsx(
    styles.checkWrapper,
    {
      [styles.active]: !disabled && checked,
      [styles.disabled]: disabled,
    },
    className,
  )

  const checkStyles = {
    ...generatedStyles,
    ...style,
  }

  return (
    <div className={checkClasses} style={checkStyles} onClick={disabled ? () => {} : onClick}>
      <Icon icon="check" $size="10px" $color="white" $opacity={checked ? 1 : 0} />
    </div>
  )
}

const Accordion = ({
  disabled = false,
  icon,
  title,
  children,
  isCollapsible = false,
  checked = false,
  helperText,
  onClick = () => {},
  isCheckable = false,
  $borderColor,
  $outerBorderColor,
  $showBorder = true,
  $withBackground = false,
  className = "",
  style = {},
  ...rest
}: BaseStyles & {
  disabled?: boolean
  icon?: string
  title: string
  helperText?: string
  isCollapsible?: boolean
  checked?: boolean
  onClick?: () => void
  isCheckable?: boolean
  children?: ReactNode
  $outerBorderColor?: string
  $showBorder?: boolean
  $withBackground?: boolean
  className?: string
  style?: React.CSSProperties
}) => {
  const [active, setActive] = useState(true)
  const generatedStyles = generateStylesFromProps(rest)

  const wrapperClasses = clsx(
    styles.wrapper,
    {
      [styles.showBorder]: $showBorder,
      [styles.withBackground]: $withBackground,
      [styles.hasHelperText]: !!helperText,
    },
    className,
  )

  const wrapperStyles = {
    ...generatedStyles,
    borderColor:
      $showBorder && ($outerBorderColor || $borderColor)
        ? `var(--color-${$outerBorderColor ?? $borderColor})`
        : undefined,
    ...style,
  }

  const contentStyles = {
    borderTopColor: $borderColor ? `var(--color-${$borderColor})` : undefined,
  }

  return (
    <div className={wrapperClasses} style={wrapperStyles}>
      {helperText && (
        <div className={styles.helperWrapper}>
          <Label $type="third" $color="white">
            {helperText}
          </Label>
        </div>
      )}
      <div className={styles.header}>
        <Row $alignItems="center" $opacity={disabled ? 0.6 : 1}>
          {icon && <IconBox icon={icon} $small $marginRight="S" />}
          <H3 $marginBottom="0">{title}</H3>
        </Row>
        {isCollapsible && (
          <IconButton
            ariaLabel={active ? "See less" : "See more"}
            icon={active ? "arrow-top" : "arrow-bottom"}
            onClick={() => setActive(!active)}
          />
        )}
        {isCheckable && (
          <AccordionCheckbox
            checked={checked}
            disabled={disabled}
            onClick={disabled ? () => {} : onClick}
            $borderColor={$borderColor}
          />
        )}
      </div>
      {active && (
        <div className={styles.content} style={contentStyles}>
          {children}
        </div>
      )}
    </div>
  )
}

export default Accordion
